import Phaser from 'phaser';
import type { Save } from '../../meta/save/schema';
import { setCrashReportsEnabled } from '../../platform/crashReports';
import { haptics } from '../../platform/haptics';
import { isNative, openUrl } from '../../platform/native';
import { ONLINE_ENABLED } from '../../platform/online';
import { notificationsAllowed, notificationsDenied } from '../../platform/notifications';
import { syncDailyReminders } from '../reminders';
import { gameAudio } from '../audio/audio';
import { REG_MENU_MODE, unlockDevArea } from '../devAccess';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import { COSMETICS, SHARE_COSMETICS } from '../../content/cosmetics';
import type { Reveal } from '../../meta/reveals';
import {
  recordShare,
  SHARE_GIFT_COINS,
  SHARE_GOAL,
  SHARE_URL,
  type ShareReward,
} from '../../meta/sharing';
import { shareLink } from '../../platform/share';
import type { RevealData } from './RevealScene';
import { wipeTo } from './WipeScene';
import { openTab, REG_SETTINGS_FROM, type NavTab } from '../ui/navChrome';

type Settings = Save['settings'];

/** Hosted copy of public/privacy.html (the stores require a public URL). */
const PRIVACY_URL = 'https://playlawnmower.com/privacy';

/** What a share of the game says (the link follows it). */
const SHARE_TEXT =
  "I've been playing lawnmower., a relaxing pixel-art mowing puzzle. Give it a try!";

interface Row {
  label: string;
  value: (s: Settings) => string;
  toggle: (s: Settings) => void;
  note?: string;
  /** Only in the native apps. */
  nativeOnly?: boolean;
  /** Not a switch: its button opens this screen of more settings. */
  opens?: 'Online';
}

const ALL_ROWS: Row[] = [
  {
    label: 'Sound effects',
    value: (s) => (s.sound ? 'On' : 'Off'),
    toggle: (s) => (s.sound = !s.sound),
  },
  {
    label: 'Ambience',
    value: (s) => (s.ambience ? 'On' : 'Off'),
    toggle: (s) => (s.ambience = !s.ambience),
    note: 'Birdsong and breeze.',
  },
  {
    label: 'Vibration',
    value: (s) => (s.haptics ? 'On' : 'Off'),
    toggle: (s) => (s.haptics = !s.haptics),
  },
  {
    label: 'Screen shake',
    value: (s) => (s.reducedShake ? 'Off' : 'On'),
    toggle: (s) => (s.reducedShake = !s.reducedShake),
  },
  {
    label: 'Controls',
    value: (s) => (s.controls === 'swipe' ? 'Swipe' : 'Tap L/R'),
    toggle: (s) => (s.controls = s.controls === 'swipe' ? 'tap' : 'swipe'),
    note: 'Tap: tap a side of the screen to turn that way.',
  },
  {
    label: 'Daily reminder',
    value: (s) => (s.dailyReminder ? 'On' : 'Off'),
    toggle: (s) => (s.dailyReminder = !s.dailyReminder),
    note: 'A nudge at 5 PM when the Daily is waiting.',
    nativeOnly: true,
  },
  {
    label: 'Crash reports',
    value: (s) => (s.crashReports ? 'On' : 'Off'),
    toggle: (s) => (s.crashReports = !s.crashReports),
    note: 'Anonymous error reports help fix bugs.',
  },
  {
    label: 'Online',
    value: () => 'More >',
    toggle: () => {},
    note: 'Scores, friends, alerts and your online data.',
    opens: 'Online',
  },
];

const REMINDER = ALL_ROWS.find((r) => r.label === 'Daily reminder')!;

/** Player settings; every change is saved immediately. */
export class SettingsScene extends Phaser.Scene {
  constructor() {
    super('Settings');
  }

  create(): void {
    this.reminderBlocked = false;
    this.friend = false;
    this.friendNote = null;
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    // Notifications switched off in the phone's Settings: the reminder shows OFF.
    void notificationsDenied().then((denied) => {
      if (!denied || !this.scene.isActive()) return;
      this.reminderBlocked = saves().save.settings.dailyReminder;
      this.redraw();
    });
  }

  private readonly redraw = (): void => {
    clearScene(this);
    this.draw();
  };

  /** Set when turning the reminder on found notifications switched off for the app. */
  private reminderBlocked = false;

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const settings = saves().save.settings;
    const ROWS = ALL_ROWS.filter(
      (r) => (!r.nativeOnly || isNative()) && (r.opens !== 'Online' || ONLINE_ENABLED),
    ).map((r) =>
      r.toggle === REMINDER.toggle && this.reminderBlocked
        ? {
            ...r,
            value: () => 'Off',
            note: "Allow lawnmower's notifications in your phone's Settings first.",
          }
        : r,
    );
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'SETTINGS', 2);
    // The TELL A FRIEND card takes the screen under the header.
    if (this.friend) {
      this.drawFriend();
      return;
    }

    // Rows in a navy panel: name on the left, a toggle on the right, an optional note below. A
    // short screen (iPad) drops the notes, then tightens the rows, so the buttons below always fit.
    const warning = (row: Row) => row.toggle === REMINDER.toggle && this.reminderBlocked;
    const plan = (notes: boolean, pitch: number) => {
      let y = 34;
      const ys: number[] = [];
      for (const row of ROWS) {
        ys.push(y);
        y += pitch;
        // Notes wrap, so space by the wrapped height, not one line.
        if (row.note && (notes || warning(row))) y += wrapLineCount(row.note, width - 28) * 10 + 2;
      }
      return { y, ys, notes };
    };
    // Two rows of buttons under the panel, then BACK.
    const buttonsH = 50;
    const fits = (end: number) => end + buttonsH <= height - 34;
    const layout = [plan(true, 20), plan(false, 20)].find((l) => fits(l.y)) ?? plan(false, 18);
    const { ys: rowsY, notes } = layout;
    let y = layout.y;
    const rowsTop = 28;
    navyPanel(g, 4, rowsTop, width - 8, y - rowsTop + 2);
    ROWS.forEach((row, i) => {
      const ry = rowsY[i]!;
      label(this, 12, ry + 4, row.label);
      const value = row.value(settings).toUpperCase();
      const opens = row.opens;
      const button = bevelButton(
        this,
        width - 12 - 56,
        ry,
        56,
        16,
        value,
        () => (opens ? wipeTo(this, opens) : this.change(row)),
        !opens && value !== 'OFF',
      );
      if (opens) button.setData('label', row.label.toUpperCase());
      if (row.note && (notes || warning(row))) {
        label(this, 12, ry + 20, row.note, 1, UI.dim).setMaxWidth(width - 28);
      }
    });

    // The full game (its offer, Restore Purchases, codes) and moving progress; telling a friend
    // and the privacy policy.
    const full = saves().save.fullGame;
    const half = Math.floor((width - 16) / 2);
    // (Gold once it is unlocked.)
    bevelButton(
      this,
      6,
      y + 8,
      half,
      18,
      'FULL GAME',
      () => wipeTo(this, 'Unlock', { backTo: 'Settings' }),
      full,
    );
    bevelButton(this, 10 + half, y + 8, half, 18, 'MOVE PROGRESS', () => wipeTo(this, 'Progress'));
    bevelButton(this, 6, y + 32, half, 18, 'TELL A FRIEND', () => this.openFriend());
    bevelButton(this, 10 + half, y + 32, half, 18, 'PRIVACY POLICY', () => openUrl(PRIVACY_URL));
    y += 48;
    // Back to the tab the gear was tapped on.
    const from = (this.registry.get(REG_SETTINGS_FROM) as NavTab | undefined) ?? 'lawns';
    bevelButton(this, 6, height - 30, width - 12, 24, 'BACK', () => openTab(this, from));
    this.devTapArea(y + 8, height - 34);
  }

  // -- Tell a friend --------------------------------------------------------------------------

  /** The TELL A FRIEND card is open, and what the last try at sharing said (or null). */
  private friend = false;
  private friendNote: string | null = null;
  private sharing = false;

  private openFriend(): void {
    this.friend = true;
    this.friendNote = null;
    this.redraw();
  }

  /** The card: what a share sends and earns, the progress to the prize, SHARE and CLOSE. */
  private drawFriend(): void {
    const { width, height } = this.scale.gameSize;
    const save = saves().save;
    const w = width - 16;
    const textW = w - 20;
    const prizes = SHARE_COSMETICS.map((id) => COSMETICS.find((c) => c.id === id)!.name);
    const lines: { text: string; color?: number }[] = [
      { text: 'Send lawnmower. to a friend. The link opens the right store on their phone.' },
      {
        text:
          save.shares > 0
            ? `First share: ${SHARE_GIFT_COINS} coins. Got it!`
            : `First share: ${SHARE_GIFT_COINS} coins.`,
        color: UI.accent,
      },
      { text: `${SHARE_GOAL} shares: ${prizes.join(' and ')}.`, color: UI.accent },
      {
        text:
          save.shares >= SHARE_GOAL
            ? `Shared ${save.shares} times. Thank you!`
            : `Shared ${save.shares} of ${SHARE_GOAL}.`,
        color: UI.dim,
      },
    ];
    if (this.friendNote) lines.push({ text: this.friendNote });
    const heights = lines.map((l) => wrapLineCount(l.text, textW) * 10 + 4);
    const cardH = 8 + 14 + heights.reduce((a, b) => a + b, 0) + 4 + 20 + 4 + 18 + 8;
    const top = Math.max(26, Math.round((height - cardH) / 2));
    const close = () => {
      if (this.sharing) return;
      this.friend = false;
      this.redraw();
    };
    this.add.zone(0, 0, width, height).setOrigin(0).setInteractive().on('pointerup', close);
    const g = this.add.graphics();
    navyPanel(g, 8, top, w, cardH);
    // (The card itself takes taps, so only a tap outside it closes it.)
    this.add.zone(8, top, w, cardH).setOrigin(0).setInteractive();
    label(this, width / 2, top + 8, 'TELL A FRIEND', 1, UI.accent).setOrigin(0.5, 0);
    let y = top + 22;
    lines.forEach((l, i) => {
      label(this, 18, y, l.text, 1, l.color ?? UI.text).setMaxWidth(textW);
      y += heights[i]!;
    });
    bevelButton(this, 18, y + 4, w - 20, 20, 'SHARE', () => void this.share(), true);
    bevelButton(this, 18, y + 28, w - 20, 18, 'CLOSE', close);
  }

  /** Opens the share sheet; a share that went through is counted, and its rewards shown. */
  private async share(): Promise<void> {
    if (this.sharing) return;
    this.sharing = true;
    const outcome = await shareLink('lawnmower.', SHARE_TEXT, SHARE_URL);
    this.sharing = false;
    if (!this.scene.isActive()) return;
    this.friendNote =
      outcome === 'copied'
        ? 'Link copied: paste it to a friend. Shares from the share sheet count toward the prize.'
        : outcome === 'failed'
          ? "Couldn't share just now."
          : null;
    if (outcome !== 'shared') {
      this.redraw();
      return;
    }
    let reward: ShareReward = { coins: 0, items: [] };
    const after = saves().update((draft) => {
      reward = recordShare(draft);
    });
    const reveals: Reveal[] = [
      ...(reward.coins > 0 ? [{ kind: 'shareGift' as const, coins: reward.coins }] : []),
      ...reward.items.map((item) => ({ kind: 'shareReward' as const, item, shares: after.shares })),
    ];
    this.redraw();
    if (reveals.length === 0) {
      haptics.tap();
      return;
    }
    const data: RevealData = { reveals, done: () => this.scene.isActive() && this.redraw() };
    this.scene.launch('Reveal', data);
    this.scene.bringToTop('Reveal');
  }

  /**
   * The way into the DEV tab on hosted and store builds: five quick taps in the blank space below
   * the privacy button open it (and keep it on for this device).
   */
  private devTapArea(top: number, bottom: number): void {
    if (bottom - top < 12) return;
    const { width } = this.scale.gameSize;
    let taps = 0;
    let last = 0;
    this.add
      .zone(0, top, width, bottom - top)
      .setOrigin(0)
      .setInteractive()
      .on('pointerup', () => {
        const now = this.time.now;
        taps = now - last < 600 ? taps + 1 : 1;
        last = now;
        if (taps < 5) return;
        taps = 0;
        unlockDevArea();
        haptics.tap();
        this.registry.set(REG_MENU_MODE, 'test');
        wipeTo(this, 'Menu');
      });
  }

  private change(row: Row): void {
    if (row.toggle === REMINDER.toggle) {
      void this.changeReminder();
      return;
    }
    const s = saves().update((draft) => row.toggle(draft.settings)).settings;
    applySettings(s);
    this.redraw();
  }

  /**
   * The Daily reminder: turning it on asks for notifications (the system prompt, the first time),
   * and stays off if they are not allowed.
   */
  private async changeReminder(): Promise<void> {
    // (Shown OFF while notifications are blocked, so a tap means on.)
    const on = this.reminderBlocked || !saves().save.settings.dailyReminder;
    this.reminderBlocked = on && !(await notificationsAllowed(true));
    if (!this.reminderBlocked) saves().update((draft) => (draft.settings.dailyReminder = on));
    syncDailyReminders();
    if (this.scene.isActive()) this.redraw();
  }
}

/** Pushes settings into the systems that use them (audio, haptics, crash reports). */
export function applySettings(s: Readonly<Settings>): void {
  gameAudio.setEnabled(s.sound, s.ambience);
  haptics.setEnabled(s.haptics);
  setCrashReportsEnabled(s.crashReports);
}
